import { Box, Container, Grid, Typography } from "@mui/material"
import { useApiGet } from "auth/useApi2"
import Button from "components/Button"
import ClpTable from "components/ClpTable"
import { SelectColumnFilter } from "components/ClpTable/ClpTableFilterTypes"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import { apiEndpoints } from "generated/apiEndpoints"
import { allEquipmentStatuses } from "generated/equipmentStatuses"
import { allRoutes } from "generated/routes"
import moment from "moment"
import { useContext, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Link, useHistory } from "react-router-dom"
import { StyledTableContainer } from "shared/styled/Global"

const equipmentStatusName = (status, t) => {
  switch (status) {
    case allEquipmentStatuses.OK:
      return t("global.works", "Works")
    case allEquipmentStatuses.Broken:
      return t("global.broken", "Broken")
    case allEquipmentStatuses.AtService:
      return t("global.on_service", "On service")
    default:
      return ""
  }
}

const Equipment = () => {
  const { t } = useTranslation()
  const routerHistory = useHistory()

  const [equipmentResult, getEquipments] = useApiGet()
  const loadingContext = useContext(GlobalLoaderContext)

  useEffect(() => {
    getEquipments(apiEndpoints.systemadmindeviceregistry.listequipments)
  }, [getEquipments])

  useEffect(() => {
    loadingContext.setLoading(() => equipmentResult.status === 1)
  })

  const columns = useMemo(
    () => [
      {
        accessorKey: "equipmentId",
        header: t("global.id", "ID")
      },
      {
        accessorKey: "equipmentType",
        header: t("global.equipment", "Equipment")
      },
      {
        accessorKey: "equipmentDescription",
        header: t("global.description", "Description")
      },
      {
        accessorKey: "connectedTo",
        header: t("global.clutch_bearings", "Clutch/Bearings")
      },
      {
        accessorKey: "company.name",
        header: t("global.company", "Company")
      },
      {
        accessorKey: "subscriptionTime",
        header: t("global.binding_time", "Binding time"),
        cell: ({ cell }) => {
          return cell.row.original.subscriptionTime
            ? moment(cell.row.original.subscriptionTime).format("YYYY-MM-DD")
            : t("global.missing_date")
        }
      },
      {
        accessorFn: (row) => {
          return row.created ? moment(row.created).format("YYYY-MM-DD HH:mm") : ""
        },
        header: t("global.created", "Created")
      },
      {
        accessorKey: "status",
        header: t("global.status", "Status"),
        filter: "equals",
        meta: {
          filter: SelectColumnFilter,
          filterHeader: t("global.show_all", "Show all"),
          filterOptions: [
            {
              name: t("global.works", "Works"),
              value: allEquipmentStatuses.OK
            },
            {
              name: t("global.broken", "Broken"),
              value: allEquipmentStatuses.Broken
            },
            {
              name: t("global.on_service", "On service"),
              value: allEquipmentStatuses.AtService
            }
          ],
          mainFilter: true
        },
        cell: ({ cell }) => {
          return equipmentStatusName(cell.row.original.status, t)
        }
      }
    ],
    []
  )

  return (
    <div>
      <Container>
        <Box>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h1">{t("global.equipment", "Equipment")}</Typography>
            <Button
              component={Link}
              to={allRoutes.SystemadminDevicesEquipmentAdd()}
              color="primary"
              variant="contained">
              {t("global.add_equipment", "Add Equipment")}
            </Button>
          </Grid>
        </Box>
      </Container>
      <StyledTableContainer>
        <ClpTable
          columns={columns}
          onRowClick={(row) => routerHistory.push(allRoutes.SystemadminDevicesEquipmentEdit(row.original.id))}
          rows={equipmentResult.status === 2 && equipmentResult.data}
          loadingState={equipmentResult.status}
        />
      </StyledTableContainer>
    </div>
  )
}

export default Equipment
